/**
 * Wordloom client entry.
 *
 * Boot order matters:
 *  1. fonts + design tokens + base styles;
 *  2. the socket module registers itself as the default transport (it only
 *     connects when a screen asks for it);
 *  3. theme + sound preferences are applied, the audio unlock is armed, the
 *     keyboard-modality flag starts tracking (lib/inputModality.ts), the
 *     session layer re-joins on every (re)connect and the view sync reacts to
 *     every accepted RoomView;
 *  4. in DEV only, `?mock=game|waiting|over|lobby` swaps in an in-memory
 *     backend (dev/mockRoom.ts) so every screen renders without a server —
 *     the whole branch is compiled out of production builds;
 *  5. React mounts.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource-variable/fraunces/full.css';
import '@fontsource-variable/inter';
import './styles/tokens.css';
import './styles/base.css';
import './net/socket';
import { App } from './App';
import { installAudioUnlock, setSoundEnabled } from './audio/sfx';
import { installViewSync } from './game/sync';
import { installInputModality } from './lib/inputModality';
import { installSession } from './net/session';
import { parseMockMode } from './router';
import { applyTheme, useUiStore } from './state/uiStore';

function installPreferences(): void {
  const ui = useUiStore.getState();
  applyTheme(ui.resolvedTheme);
  // Follow the OS theme live while the preference is "system".
  try {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', () => useUiStore.getState().syncSystemTheme());
  } catch {
    /* very old browsers: the theme simply won't follow the OS live */
  }
  setSoundEnabled(ui.sound);
  useUiStore.subscribe((state, prev) => {
    if (state.sound !== prev.sound) setSoundEnabled(state.sound);
  });
}

async function boot(): Promise<void> {
  installPreferences();
  installAudioUnlock();
  installInputModality();
  installSession();
  installViewSync();

  if (import.meta.env.DEV) {
    const mode = parseMockMode(window.location.search);
    if (mode) {
      const { installMock } = await import('./dev/mockRoom');
      installMock(mode);
    }
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('Wordloom: #root element is missing from index.html');
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void boot();
